import { fetchWithAuth } from "@/lib/http";
import logger from "@/lib/logger";

const API_BASE = "/api";

export type ModelOption = Record<string, unknown>;

export interface ModelParamConfig {
  params: Record<string, unknown>;
}

export interface BillingConfig {
  quota_limit?: number;
  quota_enabled?: boolean;
}

export interface CommonModelParams {
  generate_num?: number;
  [key: string]: unknown;
}

const jsonHeaders = { "Content-Type": "application/json" };

/** sessionStorage key for cached token dropdown data */
export const TOKEN_SESSION_KEY = "token-session-data";

export interface TokenSessionData {
  names: string[];
  keys: string[];
  groups: string[];
}

/** Save deduplicated token names/keys/groups to sessionStorage */
function saveTokenDataToSession(items: unknown[]) {
  try {
    const names = new Set<string>();
    const keys = new Set<string>();
    const groups = new Set<string>();
    for (const item of items) {
      if (!item || typeof item !== "object") continue;
      const rec = item as Record<string, unknown>;
      const name = rec.name ?? rec.token_name;
      const key = rec.key ?? rec.token_key;
      const group = rec.group ?? rec.token_group;
      if (typeof name === "string" && name) names.add(name);
      if (typeof key === "string" && key) keys.add(key);
      if (typeof group === "string" && group) groups.add(group);
    }
    const data: TokenSessionData = {
      names: [...names],
      keys: [...keys],
      groups: [...groups],
    };
    sessionStorage.setItem(TOKEN_SESSION_KEY, JSON.stringify(data));
  } catch {
    // sessionStorage not available (SSR), ignore
  }
}

export const fetchUserAppkeys = async (applicationId?: string) => {
  try {
    const url = applicationId
      ? `${API_BASE}/tokens?application_id=${encodeURIComponent(applicationId)}`
      : `${API_BASE}/tokens`;
    const res = await fetchWithAuth(url, {
      method: "GET",
      headers: jsonHeaders,
    });

    if (!res.ok) {
      const raw = (await res.text()).trim();
      // 更清晰的错误信息：包含 HTTP 状态和上游返回内容（如果有的话）
      const statusInfo = `HTTP ${res.status}${
        (res as any).statusText ? ` ${(res as any).statusText}` : ""
      }`;

      let message = `获取应用列表失败: ${statusInfo}`;

      if (raw) {
        try {
          const parsed = JSON.parse(raw) as unknown;
          if (parsed && typeof parsed === "object") {
            const record = parsed as Record<string, unknown>;
            const detail = record.detail;
            const error = record.error;
            if (typeof detail === "string" && detail.trim()) message += ` - ${detail}`;
            else if (typeof error === "string" && error.trim()) message += ` - ${error}`;
            else message += ` - ${raw}`;
          } else {
            message += ` - ${raw}`;
          }
        } catch {
          message += ` - ${raw}`;
        }
      } else {
        message += " - (empty response body)";
      }

      throw new Error(message);
    }

    const data = await res.json();

    // 支持多种返回格式：
    // 1) 后端直接返回数组
    // 2) 后端返回对象且包含 appkeys 字段
    // 3) 后端返回对象且包含 tokens 字段（兼容）
    // 4) 后端返回分页对象 {page, page_size, total, items}（new-api 格式）
    if (Array.isArray(data)) {
      saveTokenDataToSession(data);
      return data;
    } else if (data && typeof data === "object") {
      const rec = data as Record<string, unknown>;
      if (Array.isArray(rec.appkeys)) {
        saveTokenDataToSession(rec.appkeys);
        return rec.appkeys as unknown[];
      }
      if (Array.isArray(rec.tokens)) {
        saveTokenDataToSession(rec.tokens);
        return rec.tokens as unknown[];
      }
      if (Array.isArray(rec.items)) {
        saveTokenDataToSession(rec.items);
        return rec.items as unknown[];
      }
    }

    logger.warn("unexpected /api/tokens response shape, returning empty list", data);
    return [];
  } catch (err: unknown) {
    // 获取密钥列表失败，抛出错误由调用方处理
    if (err instanceof Error) throw err;
    throw new Error(String(err));
  }
};

export const createAppkey = async (
  name: string,
  appId: string,
  onModels?: ModelOption[],
  expireAt?: string,
  ipWhitelist?: string[],
  modelParams?: ModelParamConfig[],
  billing_info?: BillingConfig,
  alive: boolean = true,
  commonModelParams?: CommonModelParams,
  auto_fallback?: boolean
) => {
  try {
    if (!appId) {
      throw new Error("缺少分组ID(app_id)，请先选择分组");
    }
    const finalModels = Array.isArray(onModels) ? onModels : [];

    interface CreateAppkeyPayload {
      name: string;
      app_id: string;
      on_models: ModelOption[];
      alive?: boolean;
      expire_at?: string;
      ip_whitelist?: string[];
      model_params?: ModelParamConfig[];
      billing_info?: BillingConfig;
      common_model_params?: CommonModelParams;
      auto_fallback?: boolean;
    }

    const payload: CreateAppkeyPayload = {
      name,
      app_id: appId,
      on_models: finalModels,
      alive, // 是否禁用（默认启用）
    };

    if (expireAt) payload.expire_at = expireAt;
    if (ipWhitelist && ipWhitelist.length > 0) payload.ip_whitelist = ipWhitelist;
    if (modelParams && modelParams.length > 0) payload.model_params = modelParams;

    if (billing_info) {
      payload.billing_info = billing_info;
    }

    if (commonModelParams) {
      payload.common_model_params = commonModelParams;
    }

    if (auto_fallback !== undefined) {
      payload.auto_fallback = auto_fallback;
    }

    const res = await fetchWithAuth(`${API_BASE}/tokens/create`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const raw = await res.text();
      const statusInfo = `HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ""}`;
      let message = `创建令牌失败: ${statusInfo}`;
      try {
        const parsed = JSON.parse(raw) as unknown;
        if (parsed && typeof parsed === "object") {
          const record = parsed as Record<string, unknown>;
          const detail = record.detail;
          const error = record.error;
          if (typeof detail === "string" && detail.trim()) message += ` - ${detail}`;
          else if (typeof error === "string" && error.trim()) message += ` - ${error}`;
          else if (raw.trim()) message += ` - ${raw}`;

          // 兼容 detail 是 "{\"detail\":...}" 这种二次封装
          if (
            typeof detail === "string" &&
            detail.trim().startsWith("{") &&
            detail.includes('"detail"')
          ) {
            try {
              const inner = JSON.parse(detail) as unknown;
              if (inner && typeof inner === "object") {
                const innerRecord = inner as Record<string, unknown>;
                const innerDetail = innerRecord.detail;
                const innerError = innerRecord.error;
                if (typeof innerDetail === "string" && innerDetail.trim())
                  message += ` - ${innerDetail}`;
                else if (typeof innerError === "string" && innerError.trim())
                  message += ` - ${innerError}`;
              }
            } catch {
              // ignore
            }
          }
        } else if (raw.trim()) {
          message += ` - ${raw}`;
        }
      } catch {
        if (raw.trim()) message += ` - ${raw}`;
      }

      throw new Error(message);
    }

    const result = await res.json();
    return result;
  } catch (err: unknown) {
    if (err instanceof Error) throw err;
    throw new Error(String(err));
  }
};

export const deleteAppkey = async (tokenId: number) => {
  try {
    const res = await fetchWithAuth(`${API_BASE}/tokens/update`, {
      method: "PUT",
      headers: jsonHeaders,
      body: JSON.stringify({ removed: true, appkey: tokenId }),
    });

    if (!res.ok) {
      const errJson = await res.json().catch(() => undefined);
      const detail =
        errJson &&
        typeof errJson === "object" &&
        typeof (errJson as Record<string, unknown>).detail === "string"
          ? String((errJson as Record<string, unknown>).detail)
          : "删除令牌失败";
      throw new Error(detail);
    }

    return await res.json();
  } catch (err: unknown) {
    // 删除密钥失败，抛出错误
    if (err instanceof Error) throw err;
    throw new Error(String(err));
  }
};

export const deleteGroup = async (appId: string) => {
  try {
    const res = await fetchWithAuth(`${API_BASE}/group/update`, {
      method: "PUT",
      headers: jsonHeaders,
      body: JSON.stringify({ app_id: appId, removed: true }),
    });

    if (!res.ok) {
      const errJson = await res.json().catch(() => undefined);
      const detail =
        errJson &&
        typeof errJson === "object" &&
        typeof (errJson as Record<string, unknown>).detail === "string"
          ? String((errJson as Record<string, unknown>).detail)
          : "删除分组失败";
      throw new Error(detail);
    }

    return await res.json();
  } catch (err: unknown) {
    // 删除分组失败，抛出错误
    if (err instanceof Error) throw err;
    throw new Error(String(err));
  }
};

/**
 * 更新令牌信息
 * PUT /api/tokens/[id] (代理层)
 * @param appkey 令牌ID
 * @param data 更新的数据 (支持 alive/disabled 布尔值用于启用/禁用)
 */
export const updateAppkey = async (
  appkey: string,
  data: {
    alive?: boolean;
    name?: string;
    ip_whitelist?: string[];
    models?: unknown[];
    expire_at?: string | null;
    quota_limit?: number;
    quota_enabled?: boolean;
    numericId?: number;
  }
) => {
  try {
    // Forward `models` as-is (backend expects either empty array or backend model objects).
    const onModels = Array.isArray(data.models) ? data.models : [];

    // Backend treats presence of `alive` as a toggle-only operation (ignores other fields).
    // To avoid accidental data loss, only include `alive` in payload when it's the sole
    // field being updated (i.e., caller intends to toggle enable/disable).
    const dataKeys = Object.keys(data).filter(k => k !== "numericId" && (data as any)[k] !== undefined);
    let aliveToSend: boolean | undefined = undefined;
    if ((data as any).alive !== undefined) {
      if (dataKeys.length === 1) {
        aliveToSend = (data as any).alive as boolean;
      } else {
        logger.warn(
          "updateAppkey called with 'alive' alongside other fields; removing 'alive' from payload to avoid backend ignoring other updates"
        );
      }
    }

    const payload: Record<string, unknown> = {
      appkey,
      ip_whitelist: data.ip_whitelist ?? [],
      on_models: onModels,
    };

    if (data.numericId !== undefined) payload.id = data.numericId;
    if (aliveToSend !== undefined) payload.alive = aliveToSend;
    if (data.expire_at !== undefined) payload.expire_at = data.expire_at;
    if (data.quota_limit !== undefined) payload.quota_limit = data.quota_limit;
    if ((data as any).quota_enabled !== undefined)
      payload.quota_enabled = (data as any).quota_enabled;
    if (data.name) payload.name = data.name;

    const res = await fetchWithAuth(`${API_BASE}/tokens/update`, {
      method: "PUT",
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errJson = await res.json().catch(() => undefined);
      const detail =
        errJson &&
        typeof errJson === "object" &&
        typeof (errJson as Record<string, unknown>).detail === "string"
          ? String((errJson as Record<string, unknown>).detail)
          : "更新令牌失败";
      throw new Error(detail);
    }

    return await res.json();
  } catch (err: unknown) {
    // 更新令牌失败，抛出错误
    if (err instanceof Error) throw err;
    throw new Error(String(err));
  }
};

/**
 * 创建新的分组
 * POST /api/applications (代理层)
 * @param name 分组名称
 */
export const createApplication = async (name: string) => {
  try {
    const res = await fetchWithAuth(`${API_BASE}/group/create`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({
        name,
        order: 0,
      }),
    });

    if (!res.ok) {
      const errJson = await res.json().catch(() => undefined);
      const detail =
        errJson &&
        typeof errJson === "object" &&
        typeof (errJson as Record<string, unknown>).detail === "string"
          ? String((errJson as Record<string, unknown>).detail)
          : "创建分组失败";
      throw new Error(detail);
    }

    return await res.json();
  } catch (err: unknown) {
    // 创建应用失败，抛出错误
    if (err instanceof Error) throw err;
    throw new Error(String(err));
  }
};
